import { DaySequence, DaySequenceStatus } from '../generated/gql-types';

/** Worker checked in but no final or proposed check-out yet. → {@link clockAction} (checkout). */
export const isMissingCheckOut = (
  daySequence: Pick<
    DaySequence,
    'clockInTime' | 'clockOutTime' | 'proposedClockOutTime'
  >,
): boolean =>
  !!daySequence.clockInTime &&
  !daySequence.clockOutTime &&
  !daySequence.proposedClockOutTime;

/** Both clock times recorded on the day sequence row from worker. */
export const hasLoggedTimesheet = (
  daySequence: Pick<DaySequence, 'clockInTime' | 'clockOutTime'>,
): boolean => !!daySequence.clockInTime && !!daySequence.clockOutTime;

const isApprovedStatus = (status: DaySequenceStatus): boolean =>
  status === DaySequenceStatus.ReadyForPay ||
  status === DaySequenceStatus.PayProcessed ||
  status === DaySequenceStatus.Completed;

const isClientDisputedStatus = (status: DaySequenceStatus): boolean =>
  status === DaySequenceStatus.ProposedChange ||
  status === DaySequenceStatus.EscalatedToTenant;

export type TimesheetEditAction = 'propose' | 'resolve';

export type TimesheetCardActions = {
  showCheckout: boolean;
  showEdit: boolean;
  showApprove: boolean;
  /** Which mutation the edit modal should submit. */
  editAction: TimesheetEditAction | null;
};

/**
 * Which timesheet buttons to show and which backend mutation each maps to.
 *
 * | Scenario | Endpoint |
 * |----------|----------|
 * | Missing check-out | `clockAction` (existing clock-in + new clock-out) |
 * | Client backfill / create (no existing day sequences) | `clockAction` |
 * | Tenant backfill / create (no existing day sequences) | `createDaySequences` |
 * | Client edit logged times | `proposeDaySequenceChanges` |
 * | Tenant resolve worker dispute | `resolveDaySequenceDisputes` |
 * | Approve logged / proposed times | `approveDaySequences` |
 */
export const getTimesheetCardActions = (
  daySequence: DaySequence,
  isClientPortal: boolean,
): TimesheetCardActions => {
  if (isMissingCheckOut(daySequence)) {
    return {
      showCheckout: true,
      showEdit: false,
      showApprove: false,
      editAction: null,
    };
  }

  const hasLogged = hasLoggedTimesheet(daySequence);

  if (isClientPortal) {
    if (
      isApprovedStatus(daySequence.status) ||
      isClientDisputedStatus(daySequence.status) ||
      !hasLogged
    ) {
      return {
        showCheckout: false,
        showEdit: false,
        showApprove: false,
        editAction: null,
      };
    }

    return {
      showCheckout: false,
      showEdit: true,
      showApprove: daySequence.status === DaySequenceStatus.RequiresApproval,
      editAction: 'propose',
    };
  }

  const canApprove =
    daySequence.status === DaySequenceStatus.RequiresApproval ||
    daySequence.status === DaySequenceStatus.ProposedChange ||
    daySequence.status === DaySequenceStatus.EscalatedToTenant;

  const canEdit =
    daySequence.status === DaySequenceStatus.EscalatedToTenant ||
    daySequence.status === DaySequenceStatus.ProposedChange ||
    daySequence.status === DaySequenceStatus.RequiresApproval ||
    daySequence.status === DaySequenceStatus.InProgress ||
    daySequence.status === DaySequenceStatus.ErrorEncountered ||
    daySequence.status === DaySequenceStatus.ManualInterventionRequired ||
    daySequence.status === DaySequenceStatus.Pending;

  return {
    showCheckout: false,
    showEdit: canEdit && hasLogged,
    showApprove: canApprove && hasLogged,
    editAction:
      daySequence.status === DaySequenceStatus.EscalatedToTenant
        ? 'resolve'
        : null,
  };
};
